import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import customerTypeService from "../services/customer-type.service";
import { setMessage } from "./message";
export const fetchCustomerTypeList = createAsyncThunk(
    'customerType/fetchCustomerTypeList',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await customerTypeService.getCustomerTypeList(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data: { users, totalpage, etc. }}
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);

export const fetchCustomerTypeDropdown = createAsyncThunk(
    'customerType/fetchCustomerTypeDropdown',
    async ({ page, pagesize, search, ordering }, thunkAPI) => {
        try {
            const response = await customerTypeService.getCustomerTypeDropdown(page, pagesize, search, ordering);
            return response.data; // Assuming response contains { data: { users, totalpage, etc. }}
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);
export const fetchArchiveCustomerTypeList = createAsyncThunk(
    'customerType/fetchArchiveCustomerTypeList',
    async ({ page, pagesize, search }, thunkAPI) => {
        try {
            const response = await customerTypeService.getCustomerTypeArchiveList(page, pagesize, search);
            return response.data; // Assuming response contains { data: { users, totalpage, etc. }}
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);
export const createCustomerType = createAsyncThunk(
    'customerType/createCustomerType',
    async (categoryData, thunkAPI) => {
        try {
            const response = await customerTypeService.createCustomerType(categoryData);
            return response.data; // Assuming response contains the created category data
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);
export const editCustomerType = createAsyncThunk(
    'customerType/editCustomerType',
    async ({ id, values }, thunkAPI) => {
        try {
            const response = await customerTypeService.editCustomerType(id, values);
            return response.data; // Ensure this returns the updated category data
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const deleteCustomerType = createAsyncThunk(
    'customerType/deleteCustomerType',
    async (id, thunkAPI) => {
        try {
            await customerTypeService.deleteCustomerType(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const unArchiveCustomerType = createAsyncThunk(
    'customerType/unArchiveCustomerType',
    async (id, thunkAPI) => {
        try {
            await customerTypeService.unArchiveCustomerType(id); // Ensure `id` is used here
            return id; // Return the ID of the deleted group
        } catch (error) {
            const message =
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            thunkAPI.dispatch(setMessage(message));
            return thunkAPI.rejectWithValue();
        }
    }
);


const initialState = {
    data: [],
    list: [],
    loading: false,
    totalpage: 0,
    currentpage: 1,
    totalRecord: 0,
    status: 'idle',
    error: null,
    hasMore: true,
};

const customerTypeSlice = createSlice({
    name: 'customerType',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        resetCustomerTypeList: (state) => {
            state.list = []; // This will clear the list
        },
        resetCurrentCustomerTypePage: (state) => {
            state.currentpage = 1;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCustomerTypeList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchCustomerTypeList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload?.page || state.currentpage;
                const dataArray = action.payload?.results?.data || action.payload?.data || action.payload || [];
                state.data = Array.isArray(dataArray) ? dataArray : [];
                state.totalRecord = action.payload?.count || 0;
                state.totalPages = action.payload?.totalPages || 0;
                state.currentpage = action.payload?.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...(Array.isArray(dataArray) ? dataArray : [])];
                state.hasMore = !!action.payload?.next;
            })
            .addCase(fetchCustomerTypeList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });

            builder
            .addCase(fetchCustomerTypeDropdown.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchCustomerTypeDropdown.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload?.page || state.currentpage;
                const dataArray = action.payload?.results?.data || action.payload?.data || action.payload || [];
                state.data = Array.isArray(dataArray) ? dataArray : [];
                state.totalRecord = action.payload?.count || 0;
                state.totalPages = action.payload?.totalPages || 0;
                state.currentpage = action.payload?.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...(Array.isArray(dataArray) ? dataArray : [])];
                state.hasMore = !!action.payload?.next;
            })
            .addCase(fetchCustomerTypeDropdown.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
        builder
            .addCase(fetchArchiveCustomerTypeList.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArchiveCustomerTypeList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const fetchedPage = action.payload.page || state.currentpage;
                state.data = action.payload.results.data || [];  // Update to match your API response
                state.totalRecord = action.payload.count || 0; // Handle count (total records)
                state.totalPages = action.payload.totalPages || 0; // Handle totalPages
                state.currentpage = action.payload.next ? fetchedPage + 1 : fetchedPage;
                state.list = [...state.list, ...action.payload.results.data];
                state.hasMore = !!action.payload.next;
            })
            .addCase(fetchArchiveCustomerTypeList.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
        builder
            .addCase(createCustomerType.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(createCustomerType.fulfilled, (state, action) => {
                state.status = 'succeeded';
            })
            .addCase(createCustomerType.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
        builder
            .addCase(editCustomerType.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const index = state.data.findIndex(category => category.id === action.payload.id);
                if (index !== -1) {
                    state.data[index] = action.payload;
                }
            })
            .addCase(editCustomerType.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(deleteCustomerType.pending, state => {
                state.status = 'loading';
            })
            .addCase(deleteCustomerType.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
                state.list = state.list.filter(group => group.id !== action.payload); // Remove the deleted group from list
            })
            .addCase(deleteCustomerType.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.payload;
            });
        builder
            .addCase(unArchiveCustomerType.pending, state => {
                state.status = 'loading';
            })
            .addCase(unArchiveCustomerType.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = state.data.filter(group => group.id !== action.payload); // Remove the deleted group from state
            })
            .addCase(unArchiveCustomerType.rejected, (state, action) => {
                state.status = 'failed';
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { resetCustomerTypeList, resetCurrentCustomerTypePage } = customerTypeSlice.actions;
const { reducer } = customerTypeSlice;
export default reducer;
